<script lang="ts">
  import { createEventDispatcher, onMount } from "svelte";
  import Close from "../../assets/icons/close.svg";
  import Typography from "../Typography.svelte";
  import { buildVariations, getImage, type MenuItem, type Variation } from "../../lib/helpers/steps/menuItems";
  import { TypographyType } from "../../lib/constants/typographyMap";
  import Tick from "../../assets/icons/tick.svg";
  import Button from "../Button.svelte";
  import LeftArrow from "../Icons/LeftArrow.svelte";
  import { getCopy } from "../../lib/stores/copy";

  export let item: MenuItem | undefined = undefined;
  let selectedVariation: Variation | undefined = undefined;
  let firstVariation: string | undefined = undefined;
  let secondVariationsByFirstVariation: Record<string, Variation[]> = {};
  let allVariations: { variations: Variation[]; secondVariationsByFirstVariation: Record<string, Variation[]> };

  const dispatch = createEventDispatcher();

  onMount(() => {
    if (availableVariations.length === 1) {
      if (item?.hasMultipleOptions) {
        firstVariation = availableVariations[0].name.split("|")[0];
      } else {
        selectedVariation = availableVariations[0];
      }
    }
  });

  function onFirstVariationClick(variation: Variation | undefined) {
    if (!item) {
      return;
    }

    if (!item.hasMultipleOptions) {
      selectedVariation = variation;
    } else {
      firstVariation = variation?.name?.split("|")[0];
      selectedVariation = undefined;
    }
  }

  function onSecondVariationClick(variation: Variation | undefined) {
    selectedVariation = variation;
  }

  function close() {
    dispatch("close");
  }

  function onConfirm() {
    dispatch("confirm", selectedVariation?.name);
  }

  function variationByName(name: string) {
    const variation = item?.variations.find((variation) => variation.name === name);
    return variation;
  }

  function goToFirstVariation() {
    firstVariation = undefined;
    selectedVariation = undefined;
  }

  $: allVariations = buildVariations(item);
  $: variations = allVariations.variations ?? [];
  $: secondVariationsByFirstVariation = allVariations.secondVariationsByFirstVariation;
  $: secondVariations = firstVariation ? (secondVariationsByFirstVariation[firstVariation] ?? []) : [];
  $: availableVariations = variations.filter((variation) => variation.isAvailable);
  $: image = getImage({
    defaultImage: item?.image,
    selectedVariation,
    firstVariation,
    secondVariationsByFirstVariation
  });
  $: imageLabel =
    (selectedVariation?.visible_name || selectedVariation?.name) ??
    (item?.variations[0]?.visible_name || item?.variations[0]?.name);
  $: copy = $getCopy("layout") as Record<string, string>;
</script>

<div class="fixed flex top-0 left-0 w-full h-full justify-center items-center z-10 bg-black bg-opacity-30">
  <div
    class="flex flex-col w-full h-2/3 lg:w-fit lg:h-auto bottom-0 bg-white px-4 lg:px-10 py-10 content absolute z-100 lg:relative lg:flex-row lg:gap-10">
    <button class="absolute right-4 top-4 h-6 w-6" aria-label="close" on:click={close}>
      <img src={Close} alt="Close" />
    </button>
    <div
      role="img"
      class="hidden lg:block product-image text-center overflow-hidden rounded-2xl bg-cover bg-center"
      style={`background-image: url("${image}");`}
      title={imageLabel} />
    <div class="flex flex-col flex-1 selection-container">
      <div class="text-center lg:text-left">
        <Typography mobile={TypographyType.Headline1} desktop={TypographyType.Headline1} copy={"Select gift option"} />
        <div class="hidden lg:block subtitle">
          <Typography
            mobile={TypographyType.Body1Medium}
            desktop={TypographyType.Body1Medium}
            copy={`for ${item?.visible_name || item?.name}`} />
        </div>
      </div>
      <div class="flex h-full gap-4 overflow-y-scroll max-h-[45vh] lg:max-h-[50vh] mb-[1rem]">
        {#if !firstVariation}
          <div class="flex flex-col flex-grow gap-4 mt-4 lg:mt-6">
            {#each variations as variation}
              <button
                class="relative item flex p-4 items-center w-full rounded-lg bg-[var(--color-grayscale-10)]"
                disabled={!variation.isAvailable}
                on:click|stopPropagation={() => onFirstVariationClick(variation)}>
                <div class="w-full text-left">
                  <Typography
                    mobile={TypographyType.Headline5Medium}
                    desktop={TypographyType.Headline5Medium}
                    copy={variation.visible_name} />
                </div>
                <img src={Tick} class="w-4 h-4" class:hidden={selectedVariation?.name !== variation.name} alt="" />
              </button>
            {/each}
          </div>
        {:else}
          <div class="w-full">
            <div class="flex flex-col flex-grow gap-4 mt-4 lg:mt-6">
              {#each secondVariations as variation}
                <button
                  class="relative item flex p-4 items-center w-full rounded-lg bg-[var(--color-grayscale-10)]"
                  disabled={!variation.isAvailable}
                  on:click|stopPropagation={() => onSecondVariationClick(variationByName(variation.name))}>
                  <div class="w-full text-left">
                    <Typography
                      mobile={TypographyType.Headline5Medium}
                      desktop={TypographyType.Headline5Medium}
                      copy={variation.visible_name} />
                  </div>
                  <img src={Tick} class="w-4 h-4" class:hidden={selectedVariation?.name !== variation.name} alt="" />
                </button>
              {/each}
            </div>
          </div>
        {/if}
      </div>
      {#if firstVariation}
        <Button text class="mb-2" on:click={goToFirstVariation}><LeftArrow />{copy.backButtonCopy}</Button>
      {/if}
      <Button primary on:click={onConfirm} disabled={!selectedVariation}>Confirm</Button>
    </div>
  </div>
</div>

<style>
  .content {
    border-radius: 24px;
    box-shadow:
      0px 0px 42px 0px rgba(112, 126, 150, 0.16),
      0px 0px 9.381px 0px rgba(112, 126, 150, 0.1),
      0px 0px 2.793px 0px rgba(112, 126, 150, 0.06);
  }

  @media (min-width: 1024px) {
    .selection-container {
      min-width: 23.125rem;
    }
  }

  .product-image {
    width: 23.125rem;
    height: 23.125rem;
  }

  .subtitle {
    color: var(--color-grayscale-90);
  }

  button[disabled] {
    color: var(--color-grayscale-40);
  }
</style>
